import type { Registry as Services } from '@ember/service';
import { tKey } from 'ember-intl';
import {
  type Platform,
  PLATFORMS,
} from 'frontend-utils/utils/constants/constant-values';
import type { Rssubtype } from 'lumen/models';
import {
  MEALTYPE_TRANSLATION_KEYS,
  type MealTypeValues,
  PLATFORM_TRANSLATION_KEYS,
  STANDARD_ROOMS,
} from 'lumen/utils/constants/general-constants';
import {
  SUBTYPE_VALUES_KEY_CANCELLATION_POLICY,
  SUBTYPE_VALUES_KEY_PATTERN,
  SUBTYPE_VALUES_KEY_STRATEGY,
} from 'lumen/utils/constants/rate-strategy-constants';

export function formatStrategyValue(
  intl: Services['intl'],
  hotelAndMarketService: Services['hotel-and-market'],
  value: number,
  isAbsolute: boolean,
  isChangeValue = false,
): string | null {
  if (isAbsolute) {
    if (isChangeValue) {
      return hotelAndMarketService.formatRate(
        value,
        true,
        false,
        { maximumFractionDigits: 0, minimumFractionDigits: 0 },
        true,
      );
    }

    return hotelAndMarketService.formatRate(value, true, false);
  }

  if (isChangeValue) {
    return intl.formatNumber(value, {
      style: 'percent',
      maximumFractionDigits: 2,
      signDisplay: 'exceptZero',
    });
  }

  return intl.formatNumber(value, {
    style: 'percent',
    maximumFractionDigits: 2,
  });
}

export function formatCancellationpolicyValue(
  intl: Services['intl'],
  days: number,
  time?: string,
  isChangeValue?: boolean,
): string {
  let result = '';

  if (days === 0) {
    result = intl.t(tKey('strategy.cancellation_policy.same_day'));
  } else if (days === 1) {
    // Note: general.day translation has uppercase first letter, general.days not
    result = `${days} ${intl.t(tKey('general.day'))}`.toLowerCase();
  } else {
    result = `${intl.t(tKey('general.days'), { count: days })}`.toLowerCase();
  }

  if (time) {
    result += `, ${time}`;
  }

  if (isChangeValue && days > 0) {
    return `+${result}`;
  }

  return result;
}

/**
 * Helper function to easily format the subtype labels returned by the API
 * @param subtype : pass the reference to the subtype
 * @return {Object} : object containing two keys (one for strategies & one for cancellationpolicies). Subtypes can have different translations for strategies & cancellationpolicies.
 * Not all subtypes have both. Null is returned if it's not relevant for the passed label.
 */
export function formatSubtypeLabel(subtype: Rssubtype): {
  [key: string]: string | null;
} {
  const { intl, label } = subtype;

  const formattedSubtypeLabels: {
    [key: string]: string | null;
  } = {
    [SUBTYPE_VALUES_KEY_STRATEGY]: null,
    [SUBTYPE_VALUES_KEY_CANCELLATION_POLICY]: null,
    [SUBTYPE_VALUES_KEY_PATTERN]: null,
  };

  if (subtype.isLosType) {
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = intl.t(
      tKey('rates.nights'),
      { count: label },
    );
  }

  if (subtype.isMobileType) {
    if (
      Number(label) === PLATFORMS.PLATFORM_MOBILE_WEBSITE ||
      Number(label) === PLATFORMS.PLATFORM_MOBILE_APP
    ) {
      formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = intl
        .t(`${PLATFORM_TRANSLATION_KEYS[Number(label) as Platform]}`)
        .toString();
    } else {
      formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = label;
    }
  }

  if (subtype.isMealtypeType) {
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = intl.t(
      MEALTYPE_TRANSLATION_KEYS[label as unknown as MealTypeValues],
    );
  }

  if (subtype.isBestFlexType) {
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_CANCELLATION_POLICY] = intl.t(
      tKey('strategy.metric_block.cutoff_policy'),
    );
  }

  if (subtype.isNonRefType) {
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = intl.t(
      tKey('strategy.metric_block.discount'),
    );
  }

  if (subtype.isSemiFlexType) {
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = intl.t(
      tKey('strategy.metric_block.discount'),
    );
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_CANCELLATION_POLICY] = intl.t(
      tKey('strategy.metric_block.cutoff_policy'),
    );
  }

  if (subtype.isRoomtypeType) {
    if (STANDARD_ROOMS.indexOf(label) === -1) {
      // Custom roomtype names don't have to be translated
      formattedSubtypeLabels[SUBTYPE_VALUES_KEY_PATTERN] = label;
    } else {
      // Default roomtypes can be translated using their label e.g. for "Standard room", the label will be "standard"
      formattedSubtypeLabels[SUBTYPE_VALUES_KEY_PATTERN] = intl
        .t(`rates.roomtype.${label}`)
        .toString();
    }
  }

  if (subtype.isPosType) {
    const country = intl.formatDisplayName(label, {
      fallback: 'none',
      type: 'region',
    });

    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = country;
  }

  if (subtype.isMemberRatesType) {
    // Member rates formattedLabel is only used for reports
    formattedSubtypeLabels[SUBTYPE_VALUES_KEY_STRATEGY] = intl.t(
      `general.member_rates.${label}`,
    );
  }

  return formattedSubtypeLabels;
}

/**
 * Helper function to easily format the subtype labels returned by the API, specifically for the Compset Position Per Strategy block
 */
export function formatSubtypeLabelForCompsetPosPerStrategy(
  subtype: Rssubtype,
  intl: Services['intl'],
): string {
  const { label } = subtype;

  if (subtype.isLosType) {
    return intl.t(tKey('general.los'), {
      los: label,
    });
  }

  if (subtype.isNonRefType) {
    return intl.t(tKey('rates.nonrefundable'));
  }

  if (subtype.isSemiFlexType) {
    return intl.t(tKey('rates.semi_flex'));
  }

  // Booking and Expedia are the main OTAs in Rate Strategy. But we indeed additionally calculate mobile discount for hotelsdotcom OTA if data is available.
  // We potentially can also support mobile discount for Priceline and Agoda in Rate Strategy. However, there are no mobile shops done for Priceline and thus no data is available, Agoda is only used for Parity Strategy and is not exported to RS BigTable, so currently these 2 OTAs are not shown in RS.
  if (subtype.isMobileType) {
    if (
      Number(label) === PLATFORMS.PLATFORM_MOBILE_WEBSITE ||
      Number(label) === PLATFORMS.PLATFORM_MOBILE_APP
    ) {
      if (subtype.isMobileBookingSubtype) {
        return `${intl.t(`${PLATFORM_TRANSLATION_KEYS[Number(label) as Platform]}`)} - Booking.com`;
      }

      if (subtype.isMobileExpediaSubtype) {
        return `${intl.t(`${PLATFORM_TRANSLATION_KEYS[Number(label) as Platform]}`)} - Expedia`;
      }

      if (subtype.isMobileHotelsDotComSubtype) {
        return `${intl.t(`${PLATFORM_TRANSLATION_KEYS[Number(label) as Platform]}`)} - Hotels.com`;
      }
    }

    return intl.t(
      tKey('strategy.compset_positioning_per_strategy.mobile_label'),
      {
        ota: label,
      },
    );
  }

  if (subtype.isPosBookingSubtype) {
    const country = intl.formatDisplayName(label, {
      fallback: 'none',
      type: 'region',
    });

    return `Booking.com - ${country}`;
  }

  if (subtype.isPosExpediaSubtype) {
    const country = intl.formatDisplayName(label, {
      fallback: 'none',
      type: 'region',
    });

    return `Expedia - ${country}`;
  }

  if (subtype.isMemberRatesType) {
    if (subtype.isBranddotcomMemberRatesSubType) {
      return intl.t(tKey('general.member_rates.branddotcom'));
    }
    return intl.t(`general.member_rates.${label}`);
  }

  return label;
}

/**
 * Helper function to easily format the frequencyWeekday/frequencyWeekend values returned by the API
 */
export function formatFrequencyValue(frequencyValue: number): string {
  if (frequencyValue === 0) {
    return '0%';
  }

  // Value API returns will be between 0 & 5 (0: No discount, 1: 0%-20%, 2: 20%-40%, ...)
  return `${(frequencyValue - 1) * 20}% - ${frequencyValue * 20}%`;
}
